import React, { useState, useEffect, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { FaCheckCircle, FaTrash, FaEdit, FaFileCsv, FaFilePdf } from "react-icons/fa";

const ROLES = ["ADMIN", "SUPERVISOR"];
const USERS = ["Alice", "Bob", "Charlie", "David", "Eve"];

const TASK_TYPES = [
  "Other",
];

const TASK_KEYS = [
  "checklist_tasks",
];

const LS_KEY = "checklist_tasks";

/* ============================ STORAGE HELPERS ============================ */
const load_tasks = () =>
  TASK_KEYS.flatMap((key) => {
    try {
      return JSON.parse(localStorage.getItem(key)) || [];
    } catch (e) {
      return [];
    }
  });

const loadTasks = () => {
  try {
    return JSON.parse(localStorage.getItem(LS_KEY)) || [];
  } catch (e) {
    return [];
  }
};

const saveTasks = (tasks) => localStorage.setItem(LS_KEY, JSON.stringify(tasks));

/* ============================ MAIN COMPONENT ============================ */
export default function CheckListsDashboard() {
  /* ===== ROLE & USER ===== */
  const [role, setRole] = useState("ADMIN");
  const [currentUser, setCurrentUser] = useState("Bob");

  /* ===== TASK DATA ===== */
  const [tasks, setTasks] = useState([]);
  const [toast, setToast] = useState(null);
  const [activeTask, setActiveTask] = useState(null);
  const [showTaskModal, setShowTaskModal] = useState(false);

  /* ===== FILTERS ===== */
  const [filterTypes, setFilterTypes] = useState([]);
  const [filterCategories, setFilterCategories] = useState([]);
  const [filterAssignees, setFilterAssignees] = useState([]);
  const [filterFrom, setFilterFrom] = useState("");
  const [filterTo, setFilterTo] = useState("");
const [filterStatus, setFilterStatus] = useState("ALL");

  /* ============================ INIT ============================ */
  useEffect(() => {
    const allTasks = [...load_tasks(), ...loadTasks()];
    if (!allTasks.length) {
      const sample = generateSampleTasks();
      saveTasks(sample);
      setTasks(sample);
    } else {
      setTasks(allTasks);
    }
  }, []);

  /* ============================ DERIVED ============================ */
  const totalTasks = tasks.length;
  const openTasks = tasks.filter((t) => t.status === "OPEN").length;
  const completedTasks = tasks.filter((t) => t.status === "COMPLETED").length;

  const allCategories = [...new Set(tasks.map((t) => t.category || "Other"))];

  /* ============================ FILTERED TASKS ============================ */
  const visibleTasks = useMemo(() => {
    return tasks.filter((t) => {
      if (filterStatus !== "ALL" && t.status !== filterStatus) return false;
      if (filterTypes.length && !filterTypes.includes(t.type)) return false;
      if (filterCategories.length && !filterCategories.includes(t.category)) return false;
      if (filterAssignees.length && !filterAssignees.includes(t.assignee)) return false;
      const created = new Date(t.createdAt);
      if (filterFrom && created < new Date(filterFrom)) return false;
      if (filterTo && created > new Date(filterTo + "T23:59:59")) return false;
      if (role === "SUPERVISOR" && t.assignee !== currentUser) return false;
      return true;
    });
  }, [tasks, filterStatus, filterTypes, filterCategories, filterAssignees, filterFrom, filterTo, role, currentUser]);

  /* ============================ HELPERS ============================ */
  const fireToast = (msg, type = "info") => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 2500);
  };

  const toggleFilter = (filterArray, setFilterArray, value) => {
    if (filterArray.includes(value)) {
      setFilterArray(filterArray.filter((v) => v !== value));
    } else {
      setFilterArray([...filterArray, value]);
    }
  };

  const resetFilters = () => {
    setFilterTypes([]);
    setFilterCategories([]);
    setFilterAssignees([]);
    setFilterFrom("");
    setFilterTo("");
  };
const generateUniqueId = () => Date.now() + Math.random();

  /* ============================ TASK ACTIONS ============================ */
  const addTask = () => {
    const type = prompt("Enter task type");
    if (!type) return;
    const assignee = prompt("Assign to (Alice–Eve)");
    if (!assignee) return;
    const category = prompt("Category (concern, cap, etc)") || "Other";

    const task = {
      id: generateUniqueId(),
      type,
      assignee,
      category,
      status: "OPEN",
      comments: "",
      remarks: "",
      createdAt: new Date().toISOString(),
      completedAt: null,
    };
    const next = [...tasks, task];
    setTasks(next);
    saveTasks(next);
    fireToast("Task added", "success");
  };

  const markComplete = (id) => {
    const remarks = prompt("Completion remarks (optional)");
    const next = tasks.map((t) =>
      t.id === id
        ? { ...t, status: "COMPLETED", completedAt: new Date().toISOString(), remarks: remarks || "" }
        : t
    );
    setTasks(next);
    saveTasks(next);
    fireToast("Task completed", "success");
  };

  const deleteTask = (id) => {
    const next = tasks.filter((t) => t.id !== id);
    setTasks(next);
    saveTasks(next);
    fireToast("Task deleted", "info");
  };

  const openTaskDetails = (task) => {
    setActiveTask({ ...task });
    setShowTaskModal(true);
  };

const saveTaskDetails = () => {
  setTasks((prev) => {
    const next = prev.map((t) =>
      t.id === activeTask.id ? { ...activeTask } : t
    );
    saveTasks(next); // ✅ Save exact updated tasks
    return next;
  });
  fireToast("Task updated", "success");
  setShowTaskModal(false);
};


  /* ============================ EXPORT ============================ */
  const exportCSV = () => {
    const rows = [["Type","Category","Assignee","Status","Created","Completed","Comments","Remarks"]];
    visibleTasks.forEach(t => {
      rows.push([t.type,t.category,t.assignee,t.status,t.createdAt,t.completedAt||"",t.comments,t.remarks]);
    });
    const csv = rows.map(r => r.join(",")).join("\n");
    const blob = new Blob([csv], {type:"text/csv"});
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = "tasks.csv";
    a.click();
  };

  const exportPDF = () => window.print();

  /* ============================ UI ============================ */
  return (
    <div style={styles.page}>
      {/* TOAST */}
      <AnimatePresence>
        {toast && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 20 }}
            style={{ ...styles.toast, ...styles[toast.type] }}
          >
            {toast.msg}
          </motion.div>
        )}
      </AnimatePresence>

      <header style={styles.header}>
        <h2>📝 Task Dashboard</h2>
        {role === "SUPERVISOR" && (
          <select value={currentUser} onChange={e=>setCurrentUser(e.target.value)}>
            {USERS.map(u=> <option key={u}>{u}</option>)}
          </select>
        )}
      </header>

      {/* SUMMARY CARDS */}
      <div style={styles.summaryRow}>
        <SummaryCard title="Total" value={totalTasks} icon="📋" onClick={resetFilters}/>
        <SummaryCard title="Open" value={openTasks} icon="⏳" danger onClick={()=>{resetFilters(); setFilterStatus(["OPEN"])}}/>
        <SummaryCard title="Completed" value={completedTasks} icon="✅" onClick={()=>{resetFilters(); setFilterStatus(["COMPLETED"])}}/>
        {allCategories.map(cat => (
          <SummaryCard key={cat} title={cat} value={tasks.filter(t=>t.category===cat).length} icon="📂" onClick={()=>{resetFilters(); setFilterCategories([cat])}}/>
        ))}
        {role==="ADMIN" && <SummaryCard title="Add Task" value="+" icon="➕" onClick={addTask}/>}
      </div>

      {/* FILTER CHIPS */}
      <div style={styles.filters}>
        <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
          {TASK_TYPES.map(t=>(
            <motion.span key={t} layout style={{...styles.filterChip, background: filterTypes.includes(t) ? "#1976d2" : "#eee", color: filterTypes.includes(t) ? "#fff" : "#333"}}
              onClick={()=>toggleFilter(filterTypes,setFilterTypes,t)}
            >{t} {filterTypes.includes(t) && "×"}</motion.span>
          ))}
        </div>
        <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
          {allCategories.map(c=>(
            <motion.span key={c} layout style={{...styles.filterChip, background: filterCategories.includes(c) ? "#1976d2" : "#eee", color: filterCategories.includes(c) ? "#fff" : "#333"}}
              onClick={()=>toggleFilter(filterCategories,setFilterCategories,c)}
            >{c} {filterCategories.includes(c) && "×"}</motion.span>
          ))}
        </div>
        <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
          {USERS.map(u=>(
            <motion.span key={u} layout style={{...styles.filterChip, background: filterAssignees.includes(u) ? "#1976d2" : "#eee", color: filterAssignees.includes(u) ? "#fff" : "#333"}}
              onClick={()=>toggleFilter(filterAssignees,setFilterAssignees,u)}
            >{u} {filterAssignees.includes(u) && "×"}</motion.span>
          ))}
        </div>
        <div>
          <input type="date" value={filterFrom} onChange={e=>setFilterFrom(e.target.value)}/>
          <input type="date" value={filterTo} onChange={e=>setFilterTo(e.target.value)}/>
          <button onClick={resetFilters}>Reset</button>
          <FaFileCsv onClick={exportCSV} style={{cursor:"pointer", marginLeft:10}}/>
          <FaFilePdf onClick={exportPDF} style={{cursor:"pointer", marginLeft:10}}/>
        </div>
      </div>

    {/* TASK TABLE */}
    <div style={styles.table}>
      <AnimatePresence mode="popLayout">
        {visibleTasks.map((t) => (
          <motion.div
            key={t.id}
            layout="position"   // 🔑 VERY IMPORTANT
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.2, ease: "easeOut" }}
            style={{
              display: "flex",              // 🔑 REQUIRED
              alignItems: "center",
              justifyContent: "space-between",
              overflow: "hidden",
              marginBottom: 8,
              padding: 16,
              background: "#fff",
              borderRadius: 12,
              boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
            }}
          >
            {/* LEFT */}
            <div style={{ flex: "0.68 1 0%" }}>
              <strong>{t.category}</strong> | {t.type} | {t.createdAt}
              <br />
              <strong> {t.name || ""} | {t.branch || ""}</strong> 
              <br />
              <strong>Comments: </strong> {t.comments || "" } <br/>
              {t.assignee || ""} | <strong>Remarks: </strong> {t.remarks || "" } 
              <br />
            
            </div>

            {/* STATUS */}
            <span
              style={{
                ...styles.statusBadge,
                background: t.status === "OPEN" ? "#f44336" : "#4caf50",
                marginRight: "15px",
              }}
            >
              {t.status} <br />
              
            </span>

            {/* ACTIONS */}
            <div style={styles.actionIcons}>
              {t.status === "OPEN" && (
                <FaCheckCircle onClick={() => markComplete(t.id)} title="Complete" />
              )}
              <FaEdit onClick={() => openTaskDetails(t)} title="Edit" />
              <FaTrash onClick={() => deleteTask(t.id)} title="Delete" />
            </div>
          </motion.div>
        ))}
      </AnimatePresence>
    </div>


      {/* MODAL */}
      {showTaskModal && activeTask && (
        <div style={styles.modalOverlay}>
          <div style={styles.modal}>
            <h3>Task Details</h3>
            <label>Assignee</label>
            <input value={activeTask.assignee} onChange={e=>setActiveTask({...activeTask, assignee:e.target.value})}/>
            <label>Status</label>
            <select value={activeTask.status} onChange={e=>setActiveTask({...activeTask, status:e.target.value})}>
              <option>OPEN</option>
              <option>COMPLETED</option>
            </select>
            <label>Comments</label>
          <textarea
          rows={3}
          value={activeTask.comments}
          onChange={(e) => {
            const updated = { ...activeTask, comments: e.target.value };
            setActiveTask(updated);
            setTasks((prev) =>
              prev.map((t) => (t.id === updated.id ? updated : t))
            );
          }}
        />
          <label>Remarks</label>
        <textarea
          rows={3}
          value={activeTask.remarks}
          onChange={(e) => {
            const updated = { ...activeTask, remarks: e.target.value };
            setActiveTask(updated);
            setTasks((prev) =>
              prev.map((t) => (t.id === updated.id ? updated : t))
            );
          }}
        />
                    <div style={{display:"flex",gap:10,marginTop:12}}>
                      <button onClick={saveTaskDetails}>Save</button>
                      <button onClick={()=>setShowTaskModal(false)}>Cancel</button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          );
        }

/* ============================ SAMPLE DATA GENERATOR ============================ */
const generateSampleTasks = () => {
  const now = Date.now();
  return Array.from({length:12}).map((_,i)=>{
    const status = Math.random()>0.5?"OPEN":"COMPLETED";
    const category = Math.random()>0.6?"concern":"cap";
    return {
      id: now+i,
      type: TASK_TYPES[Math.floor(Math.random()*TASK_TYPES.length)],
      assignee: USERS[Math.floor(Math.random()*USERS.length)],
      category,
      status,
      comments:"",
      remarks:"",
      createdAt:new Date(now-Math.random()*100000000).toISOString(),
      completedAt:status==="COMPLETED"?new Date(now-Math.random()*50000000).toISOString():null,
      branch: "Online angels"
    };
  });
};

/* ============================ STYLES ============================ */
const styles = {
  page:{padding:20,fontFamily:"Inter, sans-serif",background:"#f4f6f8",minHeight:"100vh"},
  header:{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:20},
  summaryRow:{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(140px,1fr))",gap:16,marginBottom:20},
  toast:{position:"fixed",bottom:20,right:20,padding:"12px 18px",borderRadius:8,color:"#fff",zIndex:999,boxShadow:"0 6px 16px rgba(0,0,0,0.2)"},
  info:{background:"#1976d2"}, success:{background:"#4caf50"},
  filters:{display:"flex",gap:12,marginBottom:20,flexWrap:"wrap"},
  filterChip:{padding:"6px 12px",borderRadius:20,cursor:"pointer",fontSize:12,userSelect:"none",boxShadow:"0 2px 6px rgba(0,0,0,0.1)"},
 table: {
  display: "flex",
  flexDirection: "column",
},

actionIcons: {
  display: "flex",
  gap: 16,
  fontSize: 22,
  cursor: "pointer",
},

statusBadge: {
  padding: "4px 10px",
  borderRadius: 20,
  color: "#fff",
  fontSize: 12,
  marginRight: 16,
},
statusBadge:{padding:"4px 10px",borderRadius:12,color:"#fff",fontWeight:"bold",fontSize:12},
  actionIcons:{display:"flex",gap:16,fontSize:20,alignItems:"center",cursor:"pointer"},
  modalOverlay:{position:"fixed",inset:0,background:"rgba(0,0,0,0.4)",display:"flex",alignItems:"center",justifyContent:"center",zIndex:1000},
  modal:{background:"#fff",padding:20,borderRadius:12,width:400,maxHeight:"80vh",overflowY:"auto",display:"flex",flexDirection:"column",gap:8},
};

/* ============================ SUMMARY CARD ============================ */
const SummaryCard = ({ title, value, icon, onClick, danger }) => (
  <motion.div layout
    onClick={onClick}
    style={{
      ...styles.summaryCard,
      background: danger ? "linear-gradient(135deg,#f44336,#ff7961)" : "linear-gradient(135deg,#1976d2,#63a4ff)",
      cursor:onClick?"pointer":"default",
      borderRadius:16,
      color:"#fff",
      padding:16,
      display:"flex",
      justifyContent:"space-between",
      alignItems:"center",
      boxShadow:"0 10px 30px rgba(0,0,0,0.2)"
    }}
  >
    <div style={{fontSize:28}}>{icon}</div>
    <div>
      <b>{value}</b>
      <div>{title}</div>
    </div>
  </motion.div>
);
